/**
 * Refresh Stage 9b: Image Generation
 *
 * Same as creation Stage7ImageV2 but also displays the existing WordPress
 * featured image (stored at article.featured_image.existing_url during
 * document pre-population). User can keep the existing image or generate
 * a new one.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { jobsService } from '../../../services/jobsService';
import { blogRunsService } from '../../../services/blogRunsService';
import { skillsService } from '../../../services/skillsService';
import type { ArticleDocument } from '../types';

function ImageGrid({ s3Keys, selectedIdx, onSelect }: { s3Keys: string[]; selectedIdx: number | null; onSelect: (i: number) => void }) {
  const [urls, setUrls] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const newUrls: Record<number, string> = {};
      for (let i = 0; i < s3Keys.length; i++) {
        try {
          const url = await skillsService.presignView(s3Keys[i]);
          if (!cancelled) newUrls[i] = url;
        } catch {
          // presign failed
        }
      }
      if (!cancelled) {
        setUrls(newUrls);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [s3Keys]);

  return (
    <div>
      <div className="text-xs font-medium text-gray-700 mb-1">
        Generated Images ({s3Keys.length}) — click to select
      </div>
      <div className="grid grid-cols-2 gap-2">
        {s3Keys.map((key, i) => (
          <button
            key={i}
            type="button"
            className={`border-2 rounded overflow-hidden ${
              selectedIdx === i ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onSelect(i)}
          >
            {urls[i] ? (
              <img src={urls[i]} alt={`Generated image ${i + 1}`} className="w-full" />
            ) : loading ? (
              <div className="h-40 flex items-center justify-center text-xs text-gray-400">Loading image...</div>
            ) : (
              <div className="h-40 flex items-center justify-center text-xs text-red-400">Failed to load</div>
            )}
            <div className="text-[10px] text-center py-1">
              Image {i + 1} {selectedIdx === i && <span className="text-blue-600 font-medium">— Selected</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ImageControls() {
  const { document, run, refreshDocument, refreshRun, refreshArticles } = useArticleContext();
  const image = document?.article?.featured_image;
  const existingUrl = (image as any)?.existing_url;
  const existingAlt = (image as any)?.existing_alt || '';
  const [description, setDescription] = useState(image?.description || '');
  const [styleDescription, setStyleDescription] = useState(image?.style_description || '');
  const [aspectRatio, setAspectRatio] = useState(image?.aspect_ratio || '16:9');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(image?.selected_index ?? null);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // Load default style from pipeline defaults if not already set
  React.useEffect(() => {
    if (defaultsLoaded || styleDescription) return;
    blogRunsService.getPipelineDefaults()
      .then((defaults) => {
        if (!styleDescription && defaults.default_image_style) {
          setStyleDescription(defaults.default_image_style);
        }
        setDefaultsLoaded(true);
      })
      .catch(() => setDefaultsLoaded(true));
  }, [defaultsLoaded, styleDescription]);

  // Sync local state when document updates
  React.useEffect(() => {
    if (image?.description && !description) setDescription(image.description);
    if (image?.style_description && !styleDescription) setStyleDescription(image.style_description);
    if (image?.s3_keys && image.s3_keys.length > 0 && selectedIdx == null) {
      setSelectedIdx(image.selected_index ?? null);
    }
  }, [image?.description, image?.style_description, image?.s3_keys, image?.selected_index]);

  const generateImage = useCallback(async () => {
    if (!run?.run_id) return;
    setGeneratingImage(true);
    setSaveStatus(null);
    try {
      // If description is empty, backend generates it via LLM first, then generates image
      const { job_id } = await jobsService.submit('blog_stage_execute', {
        run_id: run.run_id,
        stage_id: 'refresh_9b_image',
        description: description || '',
        style_description: styleDescription,
        aspect_ratio: aspectRatio,
      });
      const finalJob = await jobsService.waitForCompletion(job_id);
      await refreshRun();
      await refreshDocument();
      if (finalJob.status === 'succeeded') await refreshArticles();
    } catch (e: any) {
      console.error('Image generation failed:', e);
    } finally {
      setGeneratingImage(false);
    }
  }, [run, description, styleDescription, aspectRatio, refreshRun, refreshDocument, refreshArticles]);

  const saveSelection = useCallback(async () => {
    if (!run?.run_id || selectedIdx == null) return;
    setSaveStatus(null);
    await blogRunsService.updateDocument(run.run_id, {
      article: {
        featured_image: {
          selected_index: selectedIdx,
        },
      },
    });
    await refreshDocument();
    await refreshArticles();
    setSaveStatus(`Image ${selectedIdx + 1} saved as featured at ${new Date().toLocaleTimeString()}`);
  }, [run, selectedIdx, refreshDocument, refreshArticles]);

  return (
    <div className="space-y-4">
      {/* Existing WordPress Image */}
      {existingUrl && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Current WordPress Featured Image</div>
          <div className="border border-gray-200 rounded overflow-hidden max-w-md">
            <img src={existingUrl} alt={existingAlt || 'Current featured image'} className="w-full" />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This is the current image on the published article. Generate a new one below or keep this one by skipping this stage.
          </p>
        </div>
      )}

      {/* Object Description (optional — auto-generated if empty) */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Object Description (optional)</div>
        <p className="text-xs text-gray-500 mb-2">What the image depicts. Leave empty to auto-generate from the article title.</p>
        <textarea
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A climbing plant growing through stacked books and a lightbulb..."
        />
      </div>

      {/* Visual Style */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Visual Style</div>
        <p className="text-xs text-gray-500 mb-2">How the image should look. Pre-populated with brand defaults.</p>
        <textarea
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          rows={3}
          value={styleDescription}
          onChange={(e) => setStyleDescription(e.target.value)}
          placeholder="Centered composition on a dark navy-black background..."
        />
      </div>

      {/* Generate Image */}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Generate New Image</div>
        <div className="flex items-center gap-3">
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
          >
            <option value="16:9">16:9</option>
            <option value="4:3">4:3</option>
            <option value="1:1">1:1</option>
            <option value="3:4">3:4</option>
            <option value="9:16">9:16</option>
          </select>
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#1a2332' }}
            disabled={generatingImage}
            onClick={generateImage}
          >
            {generatingImage ? 'Generating...' : 'Generate Image'}
          </button>
        </div>
      </div>

      {/* Generated images */}
      {image?.s3_keys && image.s3_keys.length > 0 && (
        <div>
          <ImageGrid
            s3Keys={image.s3_keys}
            selectedIdx={selectedIdx}
            onSelect={setSelectedIdx}
          />
          {selectedIdx != null && (
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={saveSelection}
              >
                Save Image {selectedIdx + 1} as Featured
              </button>
              {saveStatus && <span className="text-xs text-green-600">{saveStatus}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const image = doc.article?.featured_image;
  if (!image) {
    return <div className="text-sm text-gray-500">No featured image yet.</div>;
  }
  const hasGenerated = (image.s3_keys || []).length > 0;
  const hasExisting = Boolean((image as any)?.existing_url);
  if (!hasGenerated && !hasExisting) {
    return <div className="text-sm text-gray-500">No featured image yet.</div>;
  }
  return (
    <div className="text-xs text-gray-600 space-y-1">
      {hasExisting && !hasGenerated && (
        <div><span className="text-gray-400">Status:</span> Using existing WordPress image</div>
      )}
      {hasGenerated && (
        <>
          {image.description && <div><span className="text-gray-400">Description:</span> {image.description}</div>}
          <div><span className="text-gray-400">Images:</span> {image.s3_keys.length} generated</div>
          <div><span className="text-gray-400">Selected:</span> {image.selected_index != null ? `Image ${image.selected_index + 1}` : 'None'}</div>
          {image.aspect_ratio && <div><span className="text-gray-400">Aspect ratio:</span> {image.aspect_ratio}</div>}
        </>
      )}
    </div>
  );
}

export function RefreshImageV2() {
  return (
    <StagePanel
      stageId="refresh_9b_image"
      title="Refresh: Image"
      description="View the current featured image or generate a new one."
      showPromptEditor
      hideRunButton
      renderOutput={renderOutput}
    >
      <ImageControls />
    </StagePanel>
  );
}
